import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { Switch } from '@/components/ui/switch';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type Installment = {
  name: string;
  percentage?: number;
  fixedAmount?: number;
  daysBeforeTravel: number;
};

type Template = {
  id: string;
  name: string;
  description: string | null;
  depositType: 'PERCENTAGE' | 'FIXED_AMOUNT';
  depositValue: number;
  installments: Installment[];
  balanceDueDaysBeforeTravel: number | null;
  isDefault: boolean;
  isActive: boolean;
};

interface FormData {
  name: string;
  description: string;
  depositType: 'PERCENTAGE' | 'FIXED_AMOUNT';
  depositValue: number;
  balanceType: 'single' | 'installments';
  balanceDueDaysBeforeTravel: number;
  installments: Installment[];
  isActive: boolean;
  isDefault: boolean;
}

interface PaymentPlanTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: Template | null;
  onSuccess?: () => void;
}

export function PaymentPlanTemplateModal({
  open,
  onOpenChange,
  template,
  onSuccess,
}: PaymentPlanTemplateModalProps) {
  const { t } = useTranslation('paymentPlans');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!template;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      depositType: 'PERCENTAGE',
      depositValue: 30,
      balanceType: 'single',
      balanceDueDaysBeforeTravel: 30,
      installments: [],
      isActive: true,
      isDefault: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'installments',
  });

  const depositType = watch('depositType');
  const balanceType = watch('balanceType');

  useEffect(() => {
    if (template) {
      reset({
        name: template.name,
        description: template.description || '',
        depositType: template.depositType,
        depositValue: template.depositValue,
        balanceType: template.installments.length > 0 ? 'installments' : 'single',
        balanceDueDaysBeforeTravel: template.balanceDueDaysBeforeTravel || 30,
        installments: template.installments,
        isActive: template.isActive,
        isDefault: template.isDefault,
      });
    } else {
      reset({
        name: '',
        description: '',
        depositType: 'PERCENTAGE',
        depositValue: 30,
        balanceType: 'single',
        balanceDueDaysBeforeTravel: 30,
        installments: [],
        isActive: true,
        isDefault: false,
      });
    }
  }, [template, reset]);

  const utils = trpc.useUtils();

  const createMutation = trpc.paymentPlanTemplate.create.useMutation({
    onSuccess: () => {
      utils.paymentPlanTemplate.list.invalidate();
      showSuccessToast(t('templates.successCreated'));
      onSuccess?.();
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || t('templates.failedCreate'));
    },
  });

  const updateMutation = trpc.paymentPlanTemplate.update.useMutation({
    onSuccess: () => {
      utils.paymentPlanTemplate.list.invalidate();
      showSuccessToast(t('templates.successUpdated'));
      onSuccess?.();
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || t('templates.failedUpdate'));
    },
  });

  const onSubmit = (data: FormData) => {
    setIsSubmitting(true);

    const payload = {
      name: data.name,
      description: data.description || undefined,
      depositType: data.depositType,
      depositValue: data.depositValue,
      installments: data.balanceType === 'installments' ? data.installments : [],
      balanceDueDaysBeforeTravel:
        data.balanceType === 'single' ? data.balanceDueDaysBeforeTravel : undefined,
      isDefault: data.isDefault,
    };

    if (isEditing && template) {
      updateMutation.mutate({
        id: template.id,
        data: {
          ...payload,
          isActive: data.isActive,
        },
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const addInstallment = () => {
    append({
      name: `Payment ${fields.length + 1}`,
      percentage: 50,
      daysBeforeTravel: 14,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('templates.editTemplate') : t('templates.newTemplate')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the payment plan template settings.'
              : 'Create a new reusable payment plan template.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">{t('templates.name')} *</Label>
              <Input
                id="name"
                {...register('name', { required: 'Name is required' })}
                placeholder="e.g., Standard 50/50 Plan"
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="description">{t('templates.description')}</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
          </div>

          {/* Deposit Configuration */}
          <div className="rounded-lg border p-4">
            <h4 className="font-medium mb-4">Deposit Configuration</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('templates.depositType')}</Label>
                <Combobox
                  options={[
                    { value: 'PERCENTAGE', label: t('depositTypes.PERCENTAGE') },
                    { value: 'FIXED_AMOUNT', label: t('depositTypes.FIXED_AMOUNT') },
                  ]}
                  value={depositType}
                  onValueChange={(value) =>
                    setValue('depositType', value as 'PERCENTAGE' | 'FIXED_AMOUNT')
                  }
                  placeholder="Select type"
                  searchPlaceholder="Search..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="depositValue">{t('templates.depositValue')}</Label>
                <div className="relative">
                  <Input
                    id="depositValue"
                    type="number"
                    step={depositType === 'PERCENTAGE' ? '1' : '0.01'}
                    min="0"
                    max={depositType === 'PERCENTAGE' ? '100' : undefined}
                    {...register('depositValue', {
                      required: 'Deposit value is required',
                      valueAsNumber: true,
                    })}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {depositType === 'PERCENTAGE' ? '%' : 'TRY'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Balance Configuration */}
          <div className="rounded-lg border p-4">
            <h4 className="font-medium mb-4">Balance Payment</h4>

            <div className="mb-4 flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="single"
                  {...register('balanceType')}
                  className="h-4 w-4"
                />
                <span>Single balance payment</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="installments"
                  {...register('balanceType')}
                  className="h-4 w-4"
                />
                <span>Multiple installments</span>
              </label>
            </div>

            {balanceType === 'single' ? (
              <div className="space-y-2">
                <Label htmlFor="balanceDueDaysBeforeTravel">
                  {t('templates.balanceDue')}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="balanceDueDaysBeforeTravel"
                    type="number"
                    min="0"
                    className="w-24"
                    {...register('balanceDueDaysBeforeTravel', {
                      valueAsNumber: true,
                    })}
                  />
                  <span className="text-muted-foreground">
                    {t('common.days')} {t('common.beforeTravel')}
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-end gap-3 rounded-lg bg-muted/50 p-3"
                  >
                    <div className="flex-1 space-y-2">
                      <Label>Name</Label>
                      <Input
                        {...register(`installments.${index}.name`)}
                        placeholder="e.g., Second Payment"
                      />
                    </div>
                    <div className="w-24 space-y-2">
                      <Label>Percentage</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        {...register(`installments.${index}.percentage`, {
                          valueAsNumber: true,
                        })}
                      />
                    </div>
                    <div className="w-28 space-y-2">
                      <Label>Days Before</Label>
                      <Input
                        type="number"
                        min="0"
                        {...register(`installments.${index}.daysBeforeTravel`, {
                          valueAsNumber: true,
                        })}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addInstallment}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('templates.addInstallment')}
                </Button>
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="flex items-center gap-6">
            {isEditing && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={watch('isActive')}
                  onCheckedChange={(checked: boolean) => setValue('isActive', checked)}
                />
                <span>{t('templates.isActive')}</span>
              </label>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={watch('isDefault')}
                onCheckedChange={(checked: boolean) => setValue('isDefault', checked)}
              />
              <span>{t('templates.isDefault')}</span>
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
